import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, List, Stack, Typography } from '@mui/material';
import CommentItem from './CommentItem';
import { useUserStore } from '../../stores/userStore';
import { commentApi } from '../../api/comment';
import { trackCommentCreate } from '../../lib/gaEvents';
import { CONTENT_TYPE, PAGE_TYPE } from '../../constants/gaEnums';
import Pagination from '../common/Pagination';
import CharacterCounter from '../common/CharacterCounter';
import CustomTextareaAutosize from '../common/CustomTextareaAutosize';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import BasicButton from '../button/BasicButton';
import { saveCurrentPathAndRedirectToLoginWithScroll } from '../../utils/authUtils';
import theme from '../../theme';
import UserWithProfile from '../common/UserWithProfile';
import rightArrowIcon from '../../assets/right_b3.svg';
import SelectFilter from '../common/SelectFilter';

const COMMENT_MAX_LENGTH = 5000;

const ARTICLE_TYPE_TO_CONTENT_TYPE = {
  album_review: CONTENT_TYPE.REVIEW,
  news: CONTENT_TYPE.NEWS,
  play_list: CONTENT_TYPE.LIST,
  notice: CONTENT_TYPE.NOTICE,
};

const ARTICLE_TYPE_TO_PAGE_TYPE = {
  album_review: PAGE_TYPE.REVIEWS_POST,
  news: PAGE_TYPE.NEWS_POST,
  play_list: PAGE_TYPE.LIST_POST,
  notice: PAGE_TYPE.NOTICE_POST,
};

const COMMENT_SORT_OPTIONS = [
  { label: '좋아요순', value: 'likes' },
  { label: '최신순', value: 'latest' },
];

const CommentSection = ({
  commentData,
  articleType,
  articleId,
  contentName,
  onSubmit,
  onReply,
  onDelete,
  commentScrollId = null,
}) => {
  const location = useLocation();
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const user = useUserStore((state) => state.user);
  const [comments, setComments] = useState(commentData);

  const basePath = location.pathname.replace(/\/comment\/\d+$/, '');
  const [activeCommentScrollId, setActiveCommentScrollId] = useState(commentScrollId);
  const [newComment, setNewComment] = useState('');
  const [sortBy, setSortBy] = useState('likes');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const isLoggedIn = user !== null && Object.keys(user).length > 0;

  const handleSortChange = async (newSortBy) => {
    setSortBy(newSortBy);
    try {
      const { data } = await commentApi.getComments(articleType, articleId, newSortBy);
      setComments(data);
      setPage(1);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    }
  };

  // 댓글 기준으로 페이지네이션 적용
  const currentPageComments = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return comments.slice(startIndex, endIndex);
  }, [comments, page]);
  const totalPages = Math.ceil(comments.length / itemsPerPage);

  // activeCommentScrollId가 있을 때 해당 댓글이 있는 페이지로 자동 이동 (초기 진입 시에만)
  useEffect(() => {
    if (activeCommentScrollId) {
      // 일반 댓글에서 검색
      let targetCommentIndex = comments.findIndex(
        (comment) => comment.id === activeCommentScrollId,
      );

      // 일반 댓글에서 찾지 못한 경우 대댓글에서 검색
      if (targetCommentIndex === -1) {
        for (let i = 0; i < comments.length; i++) {
          const comment = comments[i];
          if (comment.replies && comment.replies.length > 0) {
            const replyIndex = comment.replies.findIndex(
              (reply) => reply.id === activeCommentScrollId,
            );
            if (replyIndex !== -1) {
              targetCommentIndex = i;
              break;
            }
          }
        }
      }

      if (targetCommentIndex !== -1) {
        const targetPage = Math.floor(targetCommentIndex / itemsPerPage) + 1;
        setPage(targetPage);
      }
    }
  }, [activeCommentScrollId, comments, itemsPerPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const result = await onSubmit(newComment);
      const contentType = ARTICLE_TYPE_TO_CONTENT_TYPE[articleType];
      if (contentType) {
        trackCommentCreate(
          {
            contentType,
            contentId: articleId,
            contentName,
            pageType: ARTICLE_TYPE_TO_PAGE_TYPE[articleType],
          },
          user,
        );
      }
      const isLatestSort = sortBy === 'latest';
      const updatedComments = isLatestSort ? [result, ...comments] : [...comments, result];
      setComments(updatedComments);
      setNewComment('');

      // 정렬에 따라 페이지 이동: 최신순이면 1페이지, 좋아요순이면 마지막 페이지
      if (isLatestSort) {
        setPage(1);
      } else {
        const newTotalPages = Math.ceil(updatedComments.length / itemsPerPage);
        if (newTotalPages > 1) {
          setPage(newTotalPages);
        }
      }

      // URL을 새 댓글 ID로 업데이트하고 스크롤 이동
      window.history.replaceState(null, '', `${basePath}/comment/${result.id}`);
      setActiveCommentScrollId(result.id);
    } catch (err) {
      console.error('Failed to submit comment:', err);
    }
  };

  const handleReply = async (parentId, content, mentionedUserId) => {
    try {
      const result = await onReply(parentId, content, mentionedUserId);
      const contentType = ARTICLE_TYPE_TO_CONTENT_TYPE[articleType];
      if (contentType) {
        trackCommentCreate(
          {
            contentType,
            contentId: articleId,
            contentName,
            pageType: ARTICLE_TYPE_TO_PAGE_TYPE[articleType],
          },
          user,
        );
      }
      setComments(
        comments.map((comment) =>
          comment.id === parentId
            ? { ...comment, replies: [...(comment.replies || []), result] }
            : comment,
        ),
      );

      // URL을 새 대댓글 ID로 업데이트하고 스크롤 이동
      window.history.replaceState(null, '', `${basePath}/comment/${result.id}`);
      setActiveCommentScrollId(result.id);
    } catch (err) {
      console.error('Failed to submit reply:', err);
    }
  };

  const handleEdit = async (commentId, content) => {
    try {
      const { data } = await commentApi.updateComment(commentId, content);
      setComments(
        comments.map((comment) => {
          // 일반 댓글 수정
          if (comment.id === commentId) {
            return {
              ...comment,
              content: data.content,
              updatedAt: data.updatedAt,
            };
          }
          // 대댓글 수정
          if (comment.replies) {
            return {
              ...comment,
              replies: comment.replies.map((reply) =>
                reply.id === commentId
                  ? { ...reply, content: data.content, updatedAt: data.updatedAt }
                  : reply,
              ),
            };
          }
          return comment;
        }),
      );
    } catch (err) {
      console.error('Failed to edit comment:', err);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await commentApi.deleteComment(commentId);

      // 외부 onDelete 콜백이 있으면 호출 (카운트 업데이트용)
      if (onDelete) {
        onDelete(commentId);
      }

      setComments(
        comments
          .filter((comment) => comment.id !== commentId)
          .map((comment) => {
            if (comment.replies) {
              const updatedReplies = comment.replies.filter((reply) => reply.id !== commentId);
              if (updatedReplies.length !== comment.replies.length) {
                return { ...comment, replies: updatedReplies };
              }
            }
            return comment;
          }),
      );
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const handleOnPageChange = (event, value) => {
    setPage(value);
    if (activeCommentScrollId) {
      setActiveCommentScrollId(null);
      window.history.replaceState(null, '', basePath);
    }
  };

  const handleLogin = () => {
    saveCurrentPathAndRedirectToLoginWithScroll(navigateWithScrollMemory);
  };

  return (
    <Box>
      {/* 댓글 섹션 헤더 */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: '8px',
          borderBottom: `1px solid ${theme.palette.grey.line}`,
        }}
      >
        <Typography
          fontSize={{ xs: 16, md: 18 }}
          fontWeight={'bold'}
          color={theme.palette.black[9]}
        >
          댓글 ({comments.length})
        </Typography>
        <SelectFilter
          label="정렬"
          value={sortBy}
          onChange={handleSortChange}
          options={COMMENT_SORT_OPTIONS}
        />
      </Box>

      {/* 댓글 목록 */}
      <List sx={{ width: '100%', pb: 0, pt: { xs: 0.5, md: 1.5, lg: 1.5 } }}>
        <Box>
          {currentPageComments.map((comment, index) => (
            <CommentItem
              key={`${comment.id}-${index}`}
              comment={comment}
              onReply={handleReply}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isReply={false}
              commentScrollId={activeCommentScrollId}
            />
          ))}
        </Box>
      </List>

      {/* 댓글 작성 폼 */}
      {isLoggedIn ? (
        <Box
          component="form"
          onSubmit={handleSubmit}
          data-testid="main-comment-form"
          sx={{
            mt: 1.5,
            p: { xs: 2, md: 2.5 },
            bgcolor: theme.palette.grey.bg,
          }}
        >
          <UserWithProfile
            variant="large"
            username={user?.nickname}
            profileImageUrl={user?.profileImageUrl}
            userNickname={user?.nickname}
            grade={user?.grade}
            spacing={1}
            sx={{ mb: 2 }}
          />
          <CustomTextareaAutosize
            minRows={3}
            placeholder="댓글을 작성해 주세요."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            maxLength={COMMENT_MAX_LENGTH}
            sx={{ marginBottom: '8px' }}
          />
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Box
              sx={{
                width: { xs: '100%', md: 'auto' },
                display: 'flex',
                justifyContent: { xs: 'flex-end', md: 'flex-start' },
              }}
            >
              <CharacterCounter
                currentLength={newComment?.length || 0}
                maxLength={COMMENT_MAX_LENGTH}
              />
            </Box>
            <BasicButton
              variant="contained"
              type="submit"
              disabled={!newComment.trim()}
              sx={{
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: '66px', lg: '58px' },
                height: { xs: '38px', lg: '30px' },
                padding: '8px 16px',
                typography: 'body3',
                fontWeight: 'medium',
                color: theme.palette.primary.contrastText,
              }}
            >
              등록
            </BasicButton>
          </Stack>
        </Box>
      ) : (
        <BasicButton
          variant="outlined"
          fullWidth
          onClick={handleLogin}
          sx={{
            mt: 1.5,
            justifyContent: 'flex-start',
            borderRadius: 0.5,
            height: '46px',
            padding: '12px 16px',
          }}
        >
          <Typography fontSize={14} color={theme.palette.black[1]}>
            댓글을 작성하려면 로그인 해주세요.
          </Typography>
          <Box
            component="img"
            src={rightArrowIcon}
            alt="로그인"
            sx={{ ml: 1, width: 12, height: 12 }}
          />
        </BasicButton>
      )}

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <Pagination
          count={totalPages}
          page={page}
          onChange={handleOnPageChange}
          marginTop={{ lg: '30px', md: '20px', xs: '30px' }}
        />
      )}
    </Box>
  );
};

export default CommentSection;
